"use client"
import seedrandom from "seedrandom"
import { createContext, useContext, useState, useEffect, useCallback } from "react"
import { useAudio } from "./AudioContext"
import { trackGameStart, trackGameComplete, trackCardFlip } from "../utils/analytics"

export const GameContext = createContext()

export function GameProvider({ children }) {
  // Game State
  const [cards, setCards] = useState([])
  const [flipped, setFlipped] = useState([])
  const [matched, setMatched] = useState([])
  const [startTime, setStartTime] = useState(null)
  const [completionTime, setCompletionTime] = useState(null)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [gameStarted, setGameStarted] = useState(false)
  const [bestTime, setBestTime] = useState(null)
  const [difficulty, setDifficulty] = useState("medium")
  const [memoryMode, setMemoryMode] = useState(false)
  const [memoryGrid, setMemoryGrid] = useState("4x4")
  const [hardModeTimeLimit, setHardModeTimeLimit] = useState(30)
  const [timeRemaining, setTimeRemaining] = useState(hardModeTimeLimit * 1000)
  const [gameLost, setGameLost] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showMemoryModal, setShowMemoryModal] = useState(false)
  const [confettiRunning, setConfettiRunning] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)
  const [moveHistory, setMoveHistory] = useState([])
  const [moveCount, setMoveCount] = useState(0)
  const [dailyResultsVersion, setDailyResultsVersion] = useState(0)

  // Get audio context - IMPORTANT: Make sure this is properly initialized
  const { playSound, isMuted, isMobileDevice } = useAudio()

  // Pause state
  const [gamePaused, setGamePaused] = useState(false)
  const [pauseStartTime, setPauseStartTime] = useState(null)
  const [totalPausedTime, setTotalPausedTime] = useState(0)

  // Daily challenge state
  const [isDailyChallenge, setIsDailyChallenge] = useState(false)
  const [showDailyResults, setShowDailyResults] = useState(false)
  const [dailyStreak, setDailyStreak] = useState(0)

  // Story mode state
  const [isStoryMode, setIsStoryMode] = useState(false)
  const [storyLevelIndex, setStoryLevelIndex] = useState(1)
  const [storyLevelConfig, setStoryLevelConfig] = useState(null)

  // Other state
  const [speedrunMode, setSpeedrunMode] = useState(false)
  const [showSharePanel, setShowSharePanel] = useState(false)
  const [hasPlayedGame, setHasPlayedGame] = useState(false)
  const [gameComplete, setGameComplete] = useState(false)
  const [showPauseModal, setShowPauseModal] = useState(false)
  const [winConditionTriggered, setWinConditionTriggered] = useState(false)

  // Initialize game function
  const initializeGame = useCallback(() => {
    // Reset game completion state
    setGameComplete(false)
    setWinConditionTriggered(false)

    let symbols
    let gridSize
    if (isStoryMode && storyLevelConfig) {
      // Story mode uses level configuration
      gridSize = (storyLevelConfig.rows * storyLevelConfig.cols) / 2

      // Curated distinct emojis for story mode - very different from each other
      const distinctEmojis = [
        "🎨", "🚀", "🌟", "🎯", "🔥", "💎", "🎪", "🎭",
        "🎸", "🏆", "🌈", "⚡", "🎁", "🍕", "🌺", "🦋",
        "🐙", "🎮", "🍀", "🎲", "🌙", "☀️", "🎵", "🏝️",
        "🎰", "🧲", "🔮", "💫", "🌊", "🎪"
      ]
      symbols = distinctEmojis.slice(0, gridSize)
    } else if (memoryMode) {
      switch (memoryGrid) {
        case "4x4":
          gridSize = 8
          break
        case "4x5":
          gridSize = 10
          break
        case "4x6":
          gridSize = 12
          break
        case "4x7":
          gridSize = 14
          break
        default:
          gridSize = 8
      }
      symbols = Array.from({ length: gridSize }, (_, i) => String.fromCodePoint(0x1f3a8 + i))
    } else if (isDailyChallenge) {
      const today = new Date().toISOString().split("T")[0]
      const rng = seedrandom(today)

      const dailyEasySets = [
        ["🪅", "🪸", "🎏", "🪆", "📐", "👡"],
        ["🌸", "🍓", "🧁", "🎀", "🪞", "🫧"],
        ["🌵", "🦎", "🏺", "🧿", "🪬", "🐚"],
        ["🍄", "🌰", "🦔", "🍂", "🪺", "🌿"],
        ["🎸", "🎺", "🥁", "🪘", "🎷", "🎻"],
        ["🚂", "✈️", "🚀", "⛵", "🚁", "🛸"],
        ["🦩", "🦚", "🦜", "🦋", "🐠", "🦀"],
      ]
      const dailyMediumSets = [
        ["🧆", "🍱", "🥮", "🤿", "🚼", "🃏", "🪵", "🏵️"],
        ["🌍", "🌙", "⭐", "☀️", "🌈", "⚡", "🌊", "🔥"],
        ["🎯", "🎪", "🎠", "🎡", "🎢", "🎭", "🎨", "🎬"],
        ["🍇", "🍉", "🍋", "🍑", "🍒", "🥝", "🍍", "🥭"],
        ["🦁", "🐯", "🦊", "🐺", "🦝", "🐻", "🦌", "🐼"],
        ["🏔️", "🌋", "🏝️", "🗻", "🏜️", "🌾", "🏕️", "🌁"],
        ["💎", "🔮", "🪩", "🧲", "🔭", "🧬", "⚗️", "🔑"],
      ]
      const dailyHardSets = [
        ["🤺", "🔥", "👹", "🍕", "🧮", "✏️", "💖", "📏"],
        ["🧸", "🪆", "🎲", "♟️", "🎴", "🀄", "🎰", "🃏"],
        ["🫀", "🧠", "👁️", "👂", "🦷", "🦴", "💉", "🩺"],
        ["🚦", "🗽", "🗼", "🏯", "🕌", "⛩️", "🕍", "🗿"],
        ["🌡️", "🧪", "🔬", "💡", "🔋", "🖥️", "📡", "🛰️"],
        ["🫐", "🥦", "🌽", "🥕", "🧅", "🍆", "🥑", "🫑"],
        ["🪐", "🌌", "🔭", "🌠", "☄️", "🌑", "🌕", "💫"],
      ]

      const setIndex = Math.floor(rng() * dailyEasySets.length)

      switch (difficulty) {
        case "easy":
          symbols = dailyEasySets[setIndex]
          break
        case "medium":
          symbols = dailyMediumSets[setIndex]
          break
        case "hard":
          symbols = dailyHardSets[setIndex]
          break
        default:
          symbols = dailyMediumSets[setIndex]
      }
    } else {
      switch (difficulty) {
        case "easy":
          symbols = ["🪅", "🪸", "🎏", "🪆", "📐", "👡"]
          break
        case "medium":
          symbols = ["🧆", "🍱", "🥮", "🤿", "🚼", "🃏", "🪵", "🏵️"]
          break
        case "hard":
          symbols = ["🤺", "🔥", "👹", "🍕", "🧮", "✏️", "💖", "📏"]
          break
        default:
          symbols = ["🎨", "🔥", "🎭", "🍕", "📐", "✏️", "💖", "📏"]
      }
    }
    const pairs = [...symbols, ...symbols]

    // Use seedrandom for daily challenge
    let shuffled
    if (isDailyChallenge) {
      const today = new Date().toISOString().split("T")[0]
      const rng = seedrandom(today)
      shuffled = pairs.sort(() => rng() - 0.5)
    } else {
      shuffled = pairs.sort(() => Math.random() - 0.5)
    }

    console.log('[GameContext] Setting cards:', {
      isStoryMode,
      difficulty,
      cardsLength: shuffled.length,
      willShowPreview: memoryMode || isDailyChallenge || isStoryMode
    })

    setCards(shuffled)
    if (difficulty === "easy") {
      console.log('[GameContext] Easy mode - showing preview')
      setFlipped([...Array(shuffled.length).keys()])
      setTimeout(() => setFlipped([]), 1000)
    } else {
      // Show all cards initially for memory mode, daily challenge, and story mode
      const shouldShowPreview = memoryMode || isDailyChallenge || isStoryMode
      console.log('[GameContext] Non-easy mode - shouldShowPreview:', shouldShowPreview)
      setFlipped(shouldShowPreview ? [...Array(shuffled.length).keys()] : [])
    }
    setMatched([])
    setCompletionTime(null)
    setElapsedTime(0)
    // Reset the timer display to 0 when starting a new game
    setElapsedTime(0)

    // Set time limit for story mode or hard mode
    const timeLimit = (isStoryMode && storyLevelConfig?.timeLimitSeconds)
      ? storyLevelConfig.timeLimitSeconds
      : hardModeTimeLimit
    setTimeRemaining(timeLimit * 1000)
    setGameStarted(true)
    setGameLost(false)
    setMoveCount(0) // Reset move count

    // Track game start
    const mode = memoryMode ? 'memory' : 'standard';
    trackGameStart(mode, difficulty, isDailyChallenge);

    if (memoryMode || difficulty === "easy" || isDailyChallenge || isStoryMode) {
      console.log('[GameContext] Setting timeout to flip cards back in 1 second')
      setTimeout(() => {
        console.log('[GameContext] Flipping cards back to show logo backs')
        setFlipped([])
        setStartTime(new Date())
      }, 1000)
    } else {
      console.log('[GameContext] No preview - starting immediately')
      setStartTime(new Date())
    }
  }, [difficulty, memoryMode, memoryGrid, hardModeTimeLimit, isDailyChallenge, isStoryMode, storyLevelConfig])

  // Initialize game when gameStarted changes
  useEffect(() => {
    if (gameStarted) {
      // Reset pause state when starting a new game
      setGamePaused(false)
      setTotalPausedTime(0)
      setPauseStartTime(null)

      initializeGame()
    }
  }, [gameStarted, initializeGame])

  // Load best times when difficulty changes
  useEffect(() => {
    if (difficulty === "hard") {
      const storedHardTime = localStorage.getItem("bestHardTime")
      if (storedHardTime !== null) {
        setBestTime(Number.parseFloat(storedHardTime) / 1000) // Convert from ms to seconds for display
      } else {
        setBestTime(null)
      }
    } else if (memoryMode) {
      // Handle memory mode best times based on grid size
      const storageKey = `bestMemoryTime_${memoryGrid}`
      const storedBestTime = localStorage.getItem(storageKey)
      if (storedBestTime !== null) {
        setBestTime(Number.parseFloat(storedBestTime))
      } else {
        setBestTime(null)
      }
    } else {
      // Regular easy/medium modes
      const storedBestTime = localStorage.getItem("bestTime")
      if (storedBestTime !== null) {
        setBestTime(Number.parseFloat(storedBestTime))
      } else {
        setBestTime(null)
      }
    }
  }, [difficulty, memoryMode, memoryGrid])

  // Timer functionality
  useEffect(() => {
    let timer
    if (
      startTime &&
      matched.length < cards.length &&
      !gamePaused &&
      !showDailyResults &&
      completionTime === null &&
      gameStarted
    ) {
      timer = setInterval(() => {
        const currentTime = new Date()
        const elapsed = currentTime - startTime - totalPausedTime
        setElapsedTime(elapsed)

        // Timer for hard mode OR story mode with time constraints
        const hasTimeLimit = difficulty === "hard" ||
          (isStoryMode && storyLevelConfig &&
           (storyLevelConfig.constraintMode === 'time' || storyLevelConfig.constraintMode === 'movesAndTime'))

        if (hasTimeLimit) {
          const timeLimit = (isStoryMode && storyLevelConfig?.timeLimitSeconds)
            ? storyLevelConfig.timeLimitSeconds
            : hardModeTimeLimit

          const remainingTime = timeLimit * 1000 - elapsed
          // Ensure remaining time doesn't go below 0
          const boundedRemainingTime = Math.max(0, remainingTime)
          setTimeRemaining(boundedRemainingTime)

          // End game exactly when timer hits 0
          if (boundedRemainingTime === 0) {
            playSound("lose")
            clearInterval(timer)
            setGameLost(true)
            setGameStarted(false)
          }
        }
      }, 10)
    }
    return () => clearInterval(timer)
  }, [
    startTime,
    matched.length,
    cards.length,
    difficulty,
    hardModeTimeLimit,
    gamePaused,
    totalPausedTime,
    showDailyResults,
    completionTime,
    gameStarted,
    playSound,
    isStoryMode,
    storyLevelConfig,
  ])

  // Handle card click
  const handleClick = (index) => {
    if (flipped.length < 2 && !flipped.includes(index)) {
      playSound("flip") // Play flip sound
      setFlipped([...flipped, index])
    }
  }

  // Check matches
  useEffect(() => {
    if (flipped.length === 2) {
      setMoveCount((prev) => {
        const newCount = prev + 1;
        if (process.env.NODE_ENV === "development" && isDailyChallenge) {
          console.log(`Daily challenge move ${newCount} made`);
        }
        return newCount;
      })
      const [first, second] = flipped

      if (cards[first] === cards[second]) {
        playSound("match")
        setMatched([...matched, first, second])
        setMoveHistory((prev) => {
          const newHistory = [...prev, "✅"];
          if (process.env.NODE_ENV === "development" && isDailyChallenge) {
            console.log(`Daily challenge move history updated (match):`, newHistory);
          }
          return newHistory;
        })
        // Track match
        trackCardFlip(true, moveCount + 1);
      } else {
        playSound("wrongMatch")
        setMoveHistory((prev) => {
          const newHistory = [...prev, "❌"];
          if (process.env.NODE_ENV === "development" && isDailyChallenge) {
            console.log(`Daily challenge move history updated (miss):`, newHistory);
          }
          return newHistory;
        })
        // Track mismatch
        trackCardFlip(false, moveCount + 1);
      }

      if (speedrunMode) {
        setTimeout(() => setFlipped([]), 250) // Show briefly for 100ms in Speedrun Mode
      } else {
        setTimeout(() => setFlipped([]), 500) // Normal cooldown
      }
    }
  }, [flipped, cards, speedrunMode, playSound])

  // Check story mode constraints
  useEffect(() => {
    if (isStoryMode && storyLevelConfig && gameStarted && !gameLost && !completionTime) {
      // Check move limit
      if (storyLevelConfig.constraintMode === 'moves' || storyLevelConfig.constraintMode === 'movesAndTime') {
        if (storyLevelConfig.maxMoves && moveCount > storyLevelConfig.maxMoves) {
          setGameLost(true)
          setGameStarted(false)
          playSound('wrongMatch')
        }
      }

      // Time limit handled by existing hard mode timer system
      // When timeRemaining hits 0 in hard mode or story time mode, game loss triggers
    }
  }, [isStoryMode, storyLevelConfig, gameStarted, gameLost, completionTime, moveCount, playSound])

  // Check win condition
  useEffect(() => {
    if (matched.length === cards.length && cards.length > 0 && !winConditionTriggered) {
      // Set flag immediately to prevent multiple triggers
      setWinConditionTriggered(true);
      
      // Capture move data IMMEDIATELY before any state changes to avoid race conditions
      const finalMoveCount = moveCount;
      const finalMoveHistory = [...moveHistory]; // Make a copy
      const finalMoves = finalMoveHistory.length > 0 ? finalMoveHistory.length : finalMoveCount;
      
      if (process.env.NODE_ENV === "development" && isDailyChallenge) {
        console.log("🎯 Daily challenge WIN CONDITION TRIGGERED (ONCE):", {
          finalMoveCount,
          finalMoveHistoryLength: finalMoveHistory.length,
          finalMoves
        });
      }
      
      playSound("win")
      const endTime = new Date()
      const timeTaken = (endTime - startTime - totalPausedTime) / 1000

      setConfettiRunning(true)
      setTimeout(() => setConfettiRunning(false), 8000)

      setCompletionTime(timeTaken)

      // Track game completion
      const mode = memoryMode ? 'memory' : 'standard';
      trackGameComplete(mode, difficulty, timeTaken, finalMoveCount, isDailyChallenge);

      if (isDailyChallenge) {
        try {
          const today = new Date().toISOString().split("T")[0]
          const dailyScores = JSON.parse(localStorage.getItem("dailyScores") || "{}")

          // Save using the captured values
          if (process.env.NODE_ENV === "development") {
            console.log("Saving daily challenge completion:", {
              timeTaken,
              finalMoveCount,
              finalMoveHistoryLength: finalMoveHistory.length,
              finalMoves
            })
          }
          dailyScores[today] = { time: timeTaken, moves: finalMoves }

          try {
            localStorage.setItem("dailyScores", JSON.stringify(dailyScores));
          } catch (error) {
            console.error("Failed to save daily scores to localStorage:", error);
          }
          

          // Save move history for daily challenge using captured values
          localStorage.setItem("dailyMoveHistory", JSON.stringify(finalMoveHistory))

          // Update streak calculation
          try {
            const today = new Date().toISOString().split("T")[0]
            const yesterday = new Date()
            yesterday.setDate(yesterday.getDate() - 1)
            const yesterdayKey = yesterday.toISOString().split("T")[0]

            const dailyScores = JSON.parse(localStorage.getItem("dailyScores") || "{}")

            let newStreak = 1
            if (dailyScores[yesterdayKey]) {
              newStreak = (parseInt(localStorage.getItem("dailyStreak") || "0", 10)) + 1
            }

            localStorage.setItem("dailyStreak", newStreak.toString())
            setDailyStreak(newStreak)
          } catch (error) {
            console.error("Error updating streak:", error)
          }

          // Set flag to indicate we were playing daily challenge
          localStorage.setItem("wasPlayingDailyChallenge", "true")

          // This is a duplicate of the code above, but keeping it for safety
          setTimeout(() => {
            setDailyResultsVersion((prev) => prev + 1)
            setShowDailyResults(true)
            if (process.env.NODE_ENV === "development") console.log("Daily challenge completed, showing results with streak update")
          }, 500)
        } catch (error) {
          console.error("Error saving daily challenge completion:", error)
        }
      } else {
        if (difficulty === "hard") {
          const storedHardTime = localStorage.getItem("bestHardTime")
          if (!storedHardTime || timeRemaining > Number.parseFloat(storedHardTime)) {
            localStorage.setItem("bestHardTime", timeRemaining.toString())
            setBestTime(timeRemaining / 1000) // Convert to seconds for consistent display format
          }
        } else {
          // For easy and medium modes, lower time is better
          const storageKey = memoryMode ? `bestMemoryTime_${memoryGrid}` : "bestTime"
          const storedBestTime = localStorage.getItem(storageKey)

          if (!storedBestTime || timeTaken < Number.parseFloat(storedBestTime)) {
            localStorage.setItem(storageKey, timeTaken.toString())
            setBestTime(timeTaken)
          }
        }
      }
    }
  }, [
    matched,
    cards.length,
    startTime,
    bestTime,
    isDailyChallenge,
    moveCount,
    totalPausedTime,
    difficulty,
    memoryMode,
    memoryGrid,
    timeRemaining,
    playSound,
    winConditionTriggered,
  ])

  // Check daily challenge and load daily challenge data
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0]
    const dailyScores = JSON.parse(localStorage.getItem("dailyScores") || "{}")
    const todayScore = dailyScores[today]

    // Only show results if the user explicitly clicked "View Today's Results"
    // Remove the wasPlaying check which is causing the redirect on reload
    const viewingResults = localStorage.getItem("viewingDailyResults") === "true"

    if (todayScore && viewingResults) {
      // Add a small delay to ensure all state is properly updated
      setTimeout(() => {
        setCompletionTime(todayScore.time)
        
        const savedMoveHistory = JSON.parse(localStorage.getItem("dailyMoveHistory") || "[]")
        setMoveHistory(savedMoveHistory.length > 0 ? savedMoveHistory : [])
        
        // Fallback for old daily challenges: use moveHistory.length if moves is 0 or undefined
        const storedMoves = todayScore.moves || 0
        const moves = storedMoves > 0 ? storedMoves : savedMoveHistory.length
        setMoveCount(moves)

        const currentStreak = Number(localStorage.getItem("dailyStreak") || "1")
        setDailyStreak(currentStreak)

        setShowDailyResults(true)
      }, 50)
    } else {
      if (!viewingResults) {
        setShowDailyResults(false)
      }
    }

    // Always clean up the wasPlayingDailyChallenge flag on component mount
    localStorage.removeItem("wasPlayingDailyChallenge")

    // Clean up the viewing flag after use
    return () => {
      localStorage.removeItem("viewingDailyResults")
    }
  }, [dailyResultsVersion])

  // Load daily streak
  useEffect(() => {
    const savedStreak = localStorage.getItem("dailyStreak")
    if (savedStreak) {
      setDailyStreak(Number.parseInt(savedStreak))
    }
  }, [])

  // Reset everything when browser history changes
  useEffect(() => {
    const handlePopState = () => {
      setShowDailyResults(false)
      setGameStarted(false)
      setCompletionTime(null)
      setGameComplete(false)
      setFlipped([])
      setMatched([])
      setShowModal(false)
      setMemoryMode(false)
      setDifficulty("medium")
    }

    window.addEventListener("popstate", handlePopState)

    return () => {
      window.removeEventListener("popstate", handlePopState)
    }
  }, [])

  // Reset daily challenge state when showDailyResults changes to false
  useEffect(() => {
    if (showDailyResults === false && isDailyChallenge) {
      if (process.env.NODE_ENV === "development") console.log("Resetting daily challenge state")
      // Only reset these states if we're coming from daily results
      if (completionTime !== null) {
        setCompletionTime(null)
        setMoveCount(0)
        setMoveHistory([])
        // Don't reset isDailyChallenge here as it might be needed for other logic
      }
    }
  }, [showDailyResults, isDailyChallenge, completionTime])

  // Utility Functions
  const isDailyChallengeAvailable = () => {
    const today = new Date().toISOString().split("T")[0]
    try {
      const dailyScores = JSON.parse(localStorage.getItem("dailyScores") || "{}")
      return !dailyScores[today]
    } catch (error) {
      console.error("Error checking daily challenge availability:", error)
      return true
    }
  }

  const formatTime = (time, isHardMode = false) => {
    if (completionTime !== null && !isHardMode) {
      time = Math.min(time, completionTime * 1000)
    }

    // Ensure time is never negative
    const boundedTime = Math.max(0, Math.round(time))

    const minutes = Math.floor(boundedTime / 60000)
    const seconds = Math.floor((boundedTime % 60000) / 1000)
    const milliseconds = boundedTime % 1000

    const mainPart = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    const millisPart = milliseconds.toString().padStart(3, "0")

    return {
      timeString: `${mainPart}.${millisPart}`,
      mainPart,
      millisPart,
      isRed: isHardMode && boundedTime <= 10000, // 10 seconds
    }
  }

  const formatBestTime = (time) => {
    if (time === null) return "--:--.000"

    const roundedTime = Math.round(time * 1000) // Convert to milliseconds
    const minutes = Math.floor(roundedTime / 60000)
    const seconds = Math.floor((roundedTime % 60000) / 1000)
    const milliseconds = roundedTime % 1000

    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.<span class="milliseconds">${milliseconds.toString().padStart(3, "0")}</span>`
  }

  // Game Controls
  const restartGame = () => {
    // First pause the game to stop the timer
    setGamePaused(true)

    // Reset game state
    setGameComplete(false)
    setGameLost(false)
    setFlipped([])
    setMatched([])
    setCompletionTime(null)
    setElapsedTime(0)
    setMoveCount(0)
    setTimeRemaining(hardModeTimeLimit * 1000)

    // Initialize a new game (this will reset the timer)
    initializeGame()

    // Unpause after a short delay to ensure everything is reset
    setTimeout(() => {
      setGamePaused(false)
      setTotalPausedTime(0)
      setPauseStartTime(null)
    }, 100)
  }

  const resumeGame = () => {
    setShowPauseModal(false)
    if (gamePaused) {
      const pauseDuration = new Date() - pauseStartTime
      setTotalPausedTime((prev) => prev + pauseDuration)
      setGamePaused(false)
    }
  }

  const exitGame = () => {
    setShowPauseModal(false)
    setGameStarted(false)
    setGameComplete(false)
    setGameLost(false)
    setCompletionTime(null)
    setFlipped([])
    setMatched([])
    setElapsedTime(0)
    setTimeRemaining(hardModeTimeLimit * 1000)
  }

  const closeSharePanel = () => {
    setShowSharePanel(false)
  }

  return (
    <GameContext.Provider
      value={{
        // Game state
        cards,
        flipped,
        matched,
        startTime,
        completionTime,
        elapsedTime,
        gameStarted,
        bestTime,
        difficulty,
        memoryMode,
        memoryGrid,
        hardModeTimeLimit,
        timeRemaining,
        gameLost,
        showModal,
        showMemoryModal,
        confettiRunning,
        showInstructions,
        moveHistory,
        moveCount,
        dailyResultsVersion,
        gamePaused,
        pauseStartTime,
        totalPausedTime,
        isDailyChallenge,
        showDailyResults,
        dailyStreak,
        speedrunMode,
        showSharePanel,
        hasPlayedGame,
        gameComplete,
        showPauseModal,
        isStoryMode,
        storyLevelIndex,
        storyLevelConfig,

        // Setters
        setCards,
        setFlipped,
        setMatched,
        setStartTime,
        setCompletionTime,
        setElapsedTime,
        setGameStarted,
        setBestTime,
        setDifficulty,
        setMemoryMode,
        setMemoryGrid,
        setHardModeTimeLimit,
        setTimeRemaining,
        setGameLost,
        setShowModal,
        setShowMemoryModal,
        setConfettiRunning,
        setShowInstructions,
        setMoveHistory,
        setMoveCount,
        setDailyResultsVersion,
        setGamePaused,
        setPauseStartTime,
        setTotalPausedTime,
        setIsDailyChallenge,
        setShowDailyResults,
        setDailyStreak,
        setSpeedrunMode,
        setShowSharePanel,
        setHasPlayedGame,
        setGameComplete,
        setShowPauseModal,
        setIsStoryMode,
        setStoryLevelIndex,
        setStoryLevelConfig,

        // Functions
        handleClick,
        initializeGame,
        formatTime,
        formatBestTime,
        isDailyChallengeAvailable,
        restartGame,
        resumeGame,
        exitGame,
        closeSharePanel,
      }}
    >
      {children}
    </GameContext.Provider>
  )
}

export function useGame() {
  return useContext(GameContext)
}

